import React, { useState, useEffect } from 'react';

function OfertaFlashBadge({ oferta, size = 'normal' }) {
  const [tiempoRestante, setTiempoRestante] = useState({});
  const [expirada, setExpirada] = useState(false);

  useEffect(() => {
    const calcularTiempo = () => {
      const ahora = new Date().getTime();
      const fin = new Date(oferta.fecha_fin).getTime();
      const diff = fin - ahora;

      if (diff <= 0) {
        setExpirada(true);
        return;
      }

      setTiempoRestante({
        dias: Math.floor(diff / (1000 * 60 * 60 * 24)),
        horas: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutos: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        segundos: Math.floor((diff % (1000 * 60)) / 1000)
      });
    };

    calcularTiempo();
    const interval = setInterval(calcularTiempo, 1000);
    return () => clearInterval(interval);
  }, [oferta.fecha_fin]);

  if (expirada) return null;

  // cupos_aparentes permite mostrar más vendidos de los reales (urgencia visual)
  const vendidosDisplay = Math.max(oferta.cupos_vendidos || 0, oferta.cupos_aparentes || 0);
  const cuposRestantes = oferta.cupos_oferta - vendidosDisplay;
  if (cuposRestantes <= 0) return null;

  const { dias, horas, minutos, segundos } = tiempoRestante;
  const isLarge = size === 'large';
  const isMobile = size === 'mobile';

  // Modo mobile: banner horizontal inline (sin absolute)
  if (isMobile) {
    return (
      <div
        className="w-full rounded-lg px-3 py-2 text-white"
        style={{ backgroundColor: oferta.color || '#FF4444' }}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 font-bold text-xs">
            <span>⚡</span>
            <span>{oferta.nombre}</span>
            <span className="ml-1 opacity-90">· ¡Solo {cuposRestantes} cupo{cuposRestantes !== 1 ? 's' : ''}!</span>
          </div>
          <div className="flex gap-1 text-center shrink-0">
            {dias > 0 && (
              <div className="bg-black/20 rounded px-1 py-0.5">
                <div className="text-xs font-bold leading-none">{dias}</div>
                <div className="text-[8px] opacity-80">d</div>
              </div>
            )}
            <div className="bg-black/20 rounded px-1 py-0.5">
              <div className="text-xs font-bold leading-none">{String(horas).padStart(2, '0')}</div>
              <div className="text-[8px] opacity-80">h</div>
            </div>
            <div className="bg-black/20 rounded px-1 py-0.5">
              <div className="text-xs font-bold leading-none">{String(minutos).padStart(2, '0')}</div>
              <div className="text-[8px] opacity-80">m</div>
            </div>
            <div className="bg-black/20 rounded px-1 py-0.5">
              <div className="text-xs font-bold leading-none">{String(segundos).padStart(2, '0')}</div>
              <div className="text-[8px] opacity-80">s</div>
            </div>
          </div>
        </div>
        <div className="mt-1.5">
          <div className="w-full bg-black/20 rounded-full h-1.5">
            <div
              className="h-1.5 rounded-full bg-white/70 transition-all"
              style={{ width: `${Math.min(100, (vendidosDisplay / oferta.cupos_oferta) * 100)}%` }}
            />
          </div>
          <div className="text-[9px] opacity-80 text-center mt-0.5">
            {vendidosDisplay}/{oferta.cupos_oferta} cupos vendidos
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`absolute top-0 right-0 z-10 rounded-bl-lg text-white shadow-lg ${isLarge ? 'px-4 py-3' : 'px-3 py-2'}`}
      style={{ backgroundColor: oferta.color || '#FF4444' }}
    >
      <div className={`flex items-center gap-1 font-bold ${isLarge ? 'text-sm' : 'text-xs'}`}>
        <span>⚡</span>
        <span>{oferta.nombre}</span>
      </div>

      <div className="flex gap-1 text-center mt-1">
        {dias > 0 && (
          <div className="bg-black/20 rounded px-1.5 py-0.5">
            <div className={`font-bold leading-none ${isLarge ? 'text-base' : 'text-sm'}`}>{dias}</div>
            <div className="text-[9px] opacity-80">días</div>
          </div>
        )}
        <div className="bg-black/20 rounded px-1.5 py-0.5">
          <div className={`font-bold leading-none ${isLarge ? 'text-base' : 'text-sm'}`}>{String(horas).padStart(2, '0')}</div>
          <div className="text-[9px] opacity-80">hrs</div>
        </div>
        <div className="bg-black/20 rounded px-1.5 py-0.5">
          <div className={`font-bold leading-none ${isLarge ? 'text-base' : 'text-sm'}`}>{String(minutos).padStart(2, '0')}</div>
          <div className="text-[9px] opacity-80">min</div>
        </div>
        <div className="bg-black/20 rounded px-1.5 py-0.5">
          <div className={`font-bold leading-none ${isLarge ? 'text-base' : 'text-sm'}`}>{String(segundos).padStart(2, '0')}</div>
          <div className="text-[9px] opacity-80">seg</div>
        </div>
      </div>

      <div className="mt-1 text-center">
        <span className={`font-semibold ${isLarge ? 'text-sm' : 'text-xs'}`}>
          ¡Solo {cuposRestantes} cupo{cuposRestantes !== 1 ? 's' : ''}!
        </span>
      </div>

      <div className="mt-2">
        <div className="w-full bg-black/20 rounded-full h-1.5">
          <div
            className="h-1.5 rounded-full bg-white/70 transition-all"
            style={{ width: `${Math.min(100, (vendidosDisplay / oferta.cupos_oferta) * 100)}%` }}
          />
        </div>
        <div className="text-[9px] opacity-80 text-center mt-0.5">
          {vendidosDisplay}/{oferta.cupos_oferta} cupos vendidos
        </div>
      </div>
    </div>
  );
}

export default OfertaFlashBadge;
